// Guides centre d’aide — un guide par onglet (Guides / FAQ / Réclamations).
import type { DriveStep } from 'driver.js';
import type { ProductTourDefinition } from '../types';
import { navThen } from '../mobileTourNav';
import { compactSteps, step } from '../tourStepHelpers';

/** Rétrocompat : même parcours que help_guides_v1. */
export const helpTourDefinition: ProductTourDefinition = {
  id: 'help_v1',
  ensurePath: '/aide?tab=guides',
  highlightScrollBlock: 'start',
  requiredSelectors: ['[data-tour="help-nav"]'],
  buildSteps: (ctx) => helpGuidesTourDefinition.buildSteps(ctx),
};

export const helpGuidesTourDefinition: ProductTourDefinition = {
  id: 'help_guides_v1',
  ensurePath: '/aide?tab=guides',
  highlightScrollBlock: 'start',
  requiredSelectors: ['[data-tour="help-tabs"]', '[data-tour="help-guides-catalog"]'],
  buildSteps: ({ t, navigate, pathname, layoutShell }) => [
    {
      element: '[data-tour="help-nav"]',
      popover: {
        title: t('tours.helpGuides.stepNavTitle'),
        description: t('tours.helpGuides.stepNavDesc'),
        side: 'bottom',
        align: 'end',
        onNextClick: (_e, _s, { driver: drv }) =>
          navThen('/aide?tab=guides', pathname, navigate, drv, layoutShell),
      },
    },
    step(
      '[data-tour="help-page-title"]',
      t('tours.helpGuides.stepPageTitle'),
      t('tours.helpGuides.stepPageDesc'),
      'bottom'
    ),
    step(
      '[data-tour="help-tabs"]',
      t('tours.helpGuides.stepTabsTitle'),
      t('tours.helpGuides.stepTabsDesc'),
      'bottom'
    ),
    step(
      '[data-tour="help-guides-catalog"]',
      t('tours.helpGuides.stepCatalogTitle'),
      t('tours.helpGuides.stepCatalogDesc'),
      'top'
    ),
  ],
};

export const helpFaqTourDefinition: ProductTourDefinition = {
  id: 'help_faq_v1',
  ensurePath: '/aide?tab=faq',
  highlightScrollBlock: 'start',
  requiredSelectors: ['[data-tour="help-faq"]'],
  buildSteps: ({ t, navigate, pathname, layoutShell }) => [
    {
      element: '[data-tour="help-nav"]',
      popover: {
        title: t('tours.helpFaq.stepNavTitle'),
        description: t('tours.helpFaq.stepNavDesc'),
        side: 'bottom',
        align: 'end',
        onNextClick: (_e, _s, { driver: drv }) =>
          navThen('/aide?tab=faq', pathname, navigate, drv, layoutShell),
      },
    },
    step(
      '[data-tour="help-tabs"]',
      t('tours.helpFaq.stepTabsTitle'),
      t('tours.helpFaq.stepTabsDesc'),
      'bottom'
    ),
    {
      element: '[data-tour="help-tab-faq"]',
      popover: {
        title: t('tours.helpFaq.stepTabTitle'),
        description: t('tours.helpFaq.stepTabDesc'),
        side: 'bottom',
        align: 'start',
        onNextClick: (_e, _s, { driver: drv }) => {
          navigate('/aide?tab=faq');
          window.setTimeout(() => drv.moveNext(), 450);
        },
      },
    },
    step(
      '[data-tour="help-faq"]',
      t('tours.helpFaq.stepContentTitle'),
      t('tours.helpFaq.stepContentDesc'),
      'top'
    ),
  ],
};

export const helpTicketsTourDefinition: ProductTourDefinition = {
  id: 'help_tickets_v1',
  ensurePath: '/aide?tab=tickets',
  highlightScrollBlock: 'start',
  requiredSelectors: ['[data-tour="help-new-ticket"]'],
  buildSteps: ({ t, navigate, pathname, layoutShell }) => {
    const steps: DriveStep[] = [
      {
        element: '[data-tour="help-nav"]',
        popover: {
          title: t('tours.helpTickets.stepNavTitle'),
          description: t('tours.helpTickets.stepNavDesc'),
          side: 'bottom',
          align: 'end',
          onNextClick: (_e, _s, { driver: drv }) =>
            navThen('/aide?tab=tickets', pathname, navigate, drv, layoutShell),
        },
      },
      step(
        '[data-tour="help-tabs"]',
        t('tours.helpTickets.stepTabsTitle'),
        t('tours.helpTickets.stepTabsDesc'),
        'bottom'
      ),
      {
        element: '[data-tour="help-new-ticket"]',
        popover: {
          title: t('tours.helpTickets.stepNewTitle'),
          description: t('tours.helpTickets.stepNewDesc'),
          side: 'bottom',
          align: 'end',
          onNextClick: (_e, _s, { driver: drv }) => {
            const btn = document.querySelector(
              '[data-tour="help-new-ticket"]'
            ) as HTMLElement | null;
            btn?.click();
            window.setTimeout(() => drv.moveNext(), 500);
          },
        },
      },
      step(
        '[data-tour="help-ticket-subject"]',
        t('tours.helpTickets.stepSubjectTitle'),
        t('tours.helpTickets.stepSubjectDesc')
      ),
      step(
        '[data-tour="help-ticket-category"]',
        t('tours.helpTickets.stepCategoryTitle'),
        t('tours.helpTickets.stepCategoryDesc')
      ),
      step(
        '[data-tour="help-ticket-body"]',
        t('tours.helpTickets.stepBodyTitle'),
        t('tours.helpTickets.stepBodyDesc')
      ),
      step(
        '[data-tour="help-ticket-attachments"]',
        t('tours.helpTickets.stepAttachTitle'),
        t('tours.helpTickets.stepAttachDesc')
      ),
      step(
        '[data-tour="help-tickets"]',
        t('tours.helpTickets.stepListTitle'),
        t('tours.helpTickets.stepListDesc'),
        'top'
      ),
    ];
    return compactSteps(steps);
  },
};
